import React, {
  useRef,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import _ from 'lodash';
import {Button, Checkbox, Input, Modal} from 'semantic-ui-react';

import LegacyWBIcon from './elements/LegacyWBIcon';
import PanelSettingsComponent, {LinePlotPanel} from './PanelSettings';
import * as ViewHooks from '../state/views/hooks';
import * as PanelSettingsTypes from '../state/views/panelSettings/types';
import * as PanelBankConfigActions from '../state/views/panelBankConfig/actions';
import * as PanelBankConfigTypes from '../state/views/panelBankConfig/types';
import * as RunSetViewTypes from '../state/views/runSet/types';
import * as CustomRunColorsViewTypes from '../state/views/customRunColors/types';
import * as PanelRunsLinePlot from './PanelRunsLinePlot';
import SaveSnapshotModal from './SaveSnapshotModal';
import {RunHistoryKeyInfo} from '../types/run';
import {useProjectPageQuery} from '../generated/graphql';
import {PersistentFlasher} from './elements/PersistentFlasher';
import {
  CREATE_REPORT_FLASHER_FEATURE_KEY,
  useOnboardingContext,
} from '../util/userContext';
import {useViewer} from '../state/viewer/hooks';
import {
  PanelBankContext,
  PanelBankUpdaterContext,
} from '../state/panelbank/context';
import {getFlowSectionsWithVisiblePanels} from '../util/panelbankFlow';
import makeComp from '../util/profiler';
import {isInJupyterNotebook} from '../setup';
import {panelIsActive, searchRegexFromQuery} from '../util/panelbank';
import * as RunHelpers from '../util/runhelpers';
import {LayedOutPanel} from '../util/panels';
import {PanelBankSectionConfig} from '../state/views/panelBankSectionConfig/types';

interface PanelBankControlsProps {
  entityName: string;
  projectName: string;
  readOnly?: boolean;
  panelSettingsRef: PanelSettingsTypes.Ref;
  panelBankConfigRef: PanelBankConfigTypes.Ref;
  runSetRefs: RunSetViewTypes.Ref[];
  customRunColorsRef: CustomRunColorsViewTypes.Ref;
  searchQuery: string;
  debouncedSearchQuery: string;
  singleRun?: boolean;
  historyKeyInfo: RunHistoryKeyInfo;
  panelBankWidth: number;
  setSearchQuery(searchQuery: string): void;
}

// This is the bar at the top of the PanelBank that contains search and settings
const PanelBankControls = makeComp(
  (props: PanelBankControlsProps) => {
    const {
      entityName,
      projectName,
      readOnly,
      searchQuery,
      debouncedSearchQuery,
      panelBankConfigRef,
      panelSettingsRef,
      runSetRefs,
      customRunColorsRef,
      setSearchQuery,
      singleRun,
      historyKeyInfo,
      panelBankWidth,
    } = props;

    const viewer = useViewer();

    const inputRef = useRef<Input>(null);
    useEffect(() => {
      if (inputRef && inputRef.current) {
        // Not sure why this blur and focus is necessary
        // But, without it, you can't type in the input even though it seems to maintain focus
        const innerInputRef = (inputRef.current as any).inputRef.current;
        innerInputRef.blur();
        innerInputRef.focus();
      }
    }, [searchQuery, debouncedSearchQuery]);

    const {
      settings: {autoOrganizePanels, showEmptySections},
    } = ViewHooks.usePart(panelBankConfigRef);

    const updatePanelBankSettings = ViewHooks.useViewAction(
      panelBankConfigRef,
      PanelBankConfigActions.updateSettings
    );

    const {currentPageBySectionRefID} = useContext(PanelBankContext);
    const {setExportingUnknownID} = useContext(PanelBankUpdaterContext);
    const setExporting = useCallback(() => setExportingUnknownID(true), [
      setExportingUnknownID,
    ]);

    const useExportableSections = () => {
      const {sections} = ViewHooks.useWhole(panelBankConfigRef);
      return useMemo(
        () =>
          getFlowSectionsWithVisiblePanels({
            sections,
            currentPageBySectionRefID,
            singleRun,
            searchQuery,
            historyKeyInfo,
            panelBankWidth,
          }),
        [sections]
      );
    };

    const useAllLinePlotPanels = () => {
      const {sections} = ViewHooks.useWhole(panelBankConfigRef);
      return useMemo(
        () =>
          (_.flatten(
            sections.map(s =>
              s.panels.filter(p => p.viewType === PanelRunsLinePlot.PANEL_TYPE)
            )
          ) as unknown) as LinePlotPanel[],
        [sections]
      );
    };

    const keyTypes = RunHelpers.useKeyTypes(historyKeyInfo);
    const searchRegex = searchRegexFromQuery(searchQuery);
    const usePanelIsActive = useCallback(
      (section: PanelBankSectionConfig, panel: LayedOutPanel) =>
        panelIsActive({
          section,
          panel,
          singleRun,
          searchRegex,
          historyKeyInfo,
          keyTypes,
        }),
      [singleRun, searchRegex, historyKeyInfo, keyTypes]
    );

    return (
      <div className="panel-bank__actions-wrapper">
        <div className="panel-bank__actions">
          <Input
            ref={inputRef}
            className="panel-bank__search"
            icon={<LegacyWBIcon name="search"></LegacyWBIcon>}
            tabIndex={0}
            iconPosition="left"
            value={searchQuery}
            placeholder="Search panels"
            onChange={(e, {value}) => {
              setSearchQuery(value);
            }}
          />
          <PanelSettingsComponent
            runSetRefs={runSetRefs}
            panelSettingsRef={panelSettingsRef}
            useAllLinePlotPanels={useAllLinePlotPanels}
          />
          <Modal
            trigger={
              <Button size="tiny" className={'wb-icon-button only-icon'}>
                <LegacyWBIcon name="configuration" />
              </Button>
            }>
            <Modal.Header>Workspace Settings</Modal.Header>
            <Modal.Content className="panel-bank__modal">
              <div>
                <Checkbox
                  toggle
                  label="Automatically organize panels"
                  checked={autoOrganizePanels}
                  onChange={() =>
                    updatePanelBankSettings({
                      autoOrganizePanels: !autoOrganizePanels,
                    })
                  }
                />
                <p>
                  Organize panels into sections based on metric name. For
                  example, if you log two metrics named "train/loss" and
                  "train/acc," we'll automatically group those panels in a
                  section named "train."
                </p>
              </div>
              <div>
                <Checkbox
                  toggle
                  label="Show empty sections"
                  checked={showEmptySections}
                  onChange={() =>
                    updatePanelBankSettings({
                      showEmptySections: !showEmptySections,
                    })
                  }
                />
                <p>
                  By default, we hide sections that have no available panels.
                </p>
              </div>
            </Modal.Content>
          </Modal>
          {!readOnly && viewer != null && (
            <>
              {!isInJupyterNotebook() && (
                <CreateReportButton
                  disabled={false}
                  entityName={entityName}
                  projectName={projectName}
                  onClick={setExporting}
                />
              )}
              <SaveSnapshotModal
                useExportableSections={useExportableSections}
                panelSettingsRef={panelSettingsRef}
                runSetRefs={runSetRefs}
                customRunColorsRef={customRunColorsRef}
                panelIsActive={usePanelIsActive}
              />
            </>
          )}
        </div>
      </div>
    );
  },
  {id: 'PanelBankControls'}
);

export default PanelBankControls;

const CreateReportButton: React.FC<{
  disabled: boolean;
  entityName: string;
  projectName: string;
  onClick(): void;
}> = makeComp(
  ({disabled, entityName, projectName, onClick}) => {
    const projectQuery = useProjectPageQuery({
      variables: {
        entityName,
        projectName,
      },
    });
    const runCount = projectQuery.data?.project?.runCount;

    const {onboardingState, setOnboardingState, loading} = useOnboardingContext(
      CREATE_REPORT_FLASHER_FEATURE_KEY
    );

    // Show the flashing blue dot if there are more than 20 runs
    // and the user hasn't yet clicked this button to dismiss createReport onboarding
    const flasherEnabled =
      !loading &&
      !onboardingState.createReportFlasher &&
      runCount != null &&
      runCount > 20;
    const onCreateReportClick = () => {
      onClick();
      setOnboardingState({...onboardingState, createReportFlasher: true});
    };
    return (
      <PersistentFlasher
        visible={flasherEnabled}
        offsetX={-69}
        style={{zIndex: 99}}
        trigger={
          <Button
            className="save-snapshot-button"
            primary
            onClick={onCreateReportClick}
            disabled={disabled}>
            Create report
          </Button>
        }
        popupContent={
          <>
            <div>Quickly take notes and</div>
            <div>share custom views</div>
          </>
        }
      />
    );
  },
  {id: 'CreateReportButton'}
);
